import React, {useCallback, useMemo, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {
    BtnBold,
    BtnBulletList,
    BtnClearFormatting,
    BtnItalic,
    BtnLink,
    BtnNumberedList,
    BtnRedo,
    BtnStrikeThrough,
    BtnStyles,
    BtnUnderline,
    BtnUndo,
    createButton,
    createDropdown,
    Editor,
    EditorProvider,
    Separator,
    Toolbar,
} from 'react-simple-wysiwyg';
import {Alert, Box} from '@mui/material';
import {FORM_ACCESS_LEVELS} from '../../../../../../constants/formAccessLevels';
import FORM_AUDIENCE_TYPE from '../../../../../../constants/FormAudienceType';
import {useTheme} from '@mui/material/styles';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import ColorPicker from './ColorPicker';
import ToggleColorPickerButton from './BtnToogleColorPicker';

const schoolPlaceHolder = [
    ['School Name', 'InsertText', '@SchoolName '],
];

const ALL_PLACEHOLDERS = [
    {
        key: 'parent',
        label: 'Parent Full Name',
        token: '@ParentFullName '
    },
    {
        key: 'parentSurname',
        label: 'Parent Surname',
        token: '@ParentSurname '
    },
    {
        key: 'studentForename',
        label: 'Student Forename',
        token: '@StudentForename '
    },
    {
        key: 'studentFullName',
        label: 'Student Full Name',
        token: '@StudentFullName '
    },
    {
        key: 'hisHer',
        label: 'His/Her',
        token: '@HisHer '
    },
    {
        key: 'heShe',
        label: 'He/She',
        token: '@HeShe '
    },
    {
        key: 'himHer',
        label: 'Him/Her',
        token: '@HimHer '
    },
    {
        key: 'title',
        label: 'Title',
        token: '@Title '
    },

    {
        key: 'employee',
        label: 'Employee Full Name',
        token: '@EmployeeFullName '
    },
    {
        key: 'employeeSurname',
        label: 'Employee Surname',
        token: '@EmployeeSurname '
    },
    {
        key: 'employeeAddress',
        label: 'Employee Address',
        token: '@EmployeeAddress '
    },
    {
        key: 'employeeDOB',
        label: 'Employee DOB',
        token: '@EmployeeDOB '
    },
    {
        key: 'employeeEmail',
        label: 'Employee Email',
        token: '@EmployeeEmail '
    },
    {
        key: 'employeeLegalFullName',
        label: 'Employee Legal Full Name',
        token: '@EmployeeLegalFullName '
    },
    {
        key: 'employeeLegalSurname',
        label: 'Employee Legal Surname',
        token: '@EmployeeLegalSurname '
    },
    {
        key: 'employeePhone',
        label: 'Employee Phone',
        token: '@EmployeePhone '
    },

    {
        key: 'parentContactAddress',
        label: 'Parent Address',
        token: '@ParentAddress '
    },
    {
        key: 'parentContactEmail',
        label: 'Parent Email',
        token: '@ParentEmail '
    },
    {
        key: 'parentContactPhone',
        label: 'Parent Phone',
        token: '@ParentPhone '
    },

    {
        key: 'previousSchoolEmail',
        label: 'Previous School Email',
        token: '@PreviousSchoolEmail '
    },
    {
        key: 'previousSchoolName',
        label: 'Previous School Name',
        token: '@PreviousSchoolName '
    },

    {
        key: 'studentAddress',
        label: 'Student Address',
        token: '@StudentAddress '
    },
    {
        key: 'studentApplicationStatus',
        label: 'Student Application Status',
        token: '@StudentApplicationStatus '
    },
    {
        key: 'studentBoarderStatus',
        label: 'Student Boarder Status',
        token: '@StudentBoarderStatus '
    },
    {
        key: 'studentDOB',
        label: 'Student DOB',
        token: '@StudentDOB '
    },
    {
        key: 'studentEmail',
        label: 'Student Email',
        token: '@StudentEmail '
    },
    {
        key: 'studentForm',
        label: 'Student Form',
        token: '@StudentForm '
    },
    {
        key: 'studentHouse',
        label: 'Student House',
        token: '@StudentHouse '
    },
    {
        key: 'studentIntakeGroup',
        label: 'Student Intake Group',
        token: '@StudentIntakeGroup '
    },
    {
        key: 'studentLegalForename',
        label: 'Student Legal Forename',
        token: '@StudentLegalForename '
    },
    {
        key: 'studentLegalFullName',
        label: 'Student Legal Full Name',
        token: '@StudentLegalFullName '
    },
    {
        key: 'studentUPN',
        label: 'Student UPN',
        token: '@StudentUPN '
    },
    {
        key: 'studentYearGroup',
        label: 'Student Year Group',
        token: '@StudentYearGroup '
    },
];

const getAudienceType = (audience, recipientType) => {
    const studentCore = [
        'studentForename',
        'studentFullName',
        'studentLegalForename',
        'studentLegalFullName',
        'studentUPN',
        'studentDOB',
        'studentYearGroup',
        'studentForm',
        'studentEmail',
        'studentAddress',
        'studentHouse',
        'studentBoarderStatus',
        'previousSchoolName',
        'previousSchoolEmail',
        'studentApplicationStatus',
        'studentIntakeGroup',
    ];

    const studentPronouns = ['hisHer', 'heShe', 'himHer'];
    const parentContactOnly = ['parentContactAddress', 'parentContactEmail', 'parentContactPhone'];

    const employeePlaceholders = [
        'title',
        'employee',
        'employeeSurname',
        'employeeLegalSurname',
        'employeeLegalFullName',
        'employeeDOB',
        'employeeEmail',
        'employeePhone',
        'employeeAddress',
    ];

    const parentExtras = ['parent', 'parentSurname', 'title'];

    const allKeys = [
        ...new Set([
            ...studentCore,
            ...studentPronouns,
            ...parentContactOnly,
            ...employeePlaceholders,
            ...parentExtras,
        ]),
    ];

    switch (audience) {
        case FORM_AUDIENCE_TYPE.none:
            return allKeys;
        case FORM_AUDIENCE_TYPE.students:
            return studentCore;
        case FORM_AUDIENCE_TYPE.parentsForChildCompletion:
            return [...studentCore, ...studentPronouns, ...parentContactOnly, ...parentExtras];
        case FORM_AUDIENCE_TYPE.employees:
            return employeePlaceholders;
        case FORM_AUDIENCE_TYPE.previousSchool:
            return [...studentCore, ...studentPronouns];
        case FORM_AUDIENCE_TYPE.externalUsers:
            if (!recipientType) {
                return [...studentCore, ...studentPronouns, ...employeePlaceholders];
            }
            if (recipientType === 'student') return [...studentCore, ...studentPronouns];
            if (recipientType === 'employee') return employeePlaceholders;
            return [];
        default:
            return [];
    }
};

const getAudienceLabel = (audience) => {
    switch (audience) {
        case FORM_AUDIENCE_TYPE.students:
            return 'Students';
        case FORM_AUDIENCE_TYPE.parentsForChildCompletion:
            return 'Parents';
        case FORM_AUDIENCE_TYPE.employees:
            return 'Employees';
        case FORM_AUDIENCE_TYPE.previousSchool:
            return 'Previous school';
        case FORM_AUDIENCE_TYPE.externalUsers:
            return 'External users';
        default:
            return 'audience';
    }
};

const getRecipientTypeLabel = (recipientType) => {
    if (!recipientType) return '';
    const v = String(recipientType).toLowerCase();
    if (v === 'student') return 'Student';
    if (v === 'employee') return 'Employee';
    return recipientType;
};

const placeholderIsPresent = (html, token, isLegacy = false) => {
    if (!html || !token) return false;

    if (isLegacy && token === '@Parent') return /@Parent(?![A-Za-z])/g.test(html);
    if (isLegacy && token === '@Employee') return /@Employee(?![A-Za-z])/g.test(html);

    return html.includes(token);
};

const BtnAlignCenter = createButton(
    'Align center',
    <FormatAlignCenterIcon fontSize="small" sx={{marginTop: '3px'}}/>,
    'justifyCenter'
);
const BtnAlignLeft = createButton(
    'Align left',
    <FormatAlignLeftIcon fontSize="small" sx={{marginTop: '3px'}}/>,
    'justifyLeft'
);
const BtnAlignRight = createButton(
    'Align right',
    <FormatAlignRightIcon fontSize="small" sx={{marginTop: '3px'}}/>,
    'justifyRight'
);

const RichEditor = ({
                        currentDescription,
                        setCurrentDescription,
                        isFormLive,
                        accessLevel,
                        style,
                        additionalPlaceholders = [],
                        audience,
                        recipientType,
                    }) => {
    const theme = useTheme();

    const [textColor, setTextColor] = useState('#000000');
    const [colorAnchorEl, setColorAnchorEl] = useState(null);
    const isColorPickerOpen = Boolean(colorAnchorEl);

    const selectionRef = useRef(null);
    const editableRef = useRef(null);

    const audienceType = getAudienceType(audience, recipientType);
    const isPublicAccess = accessLevel === FORM_ACCESS_LEVELS.public;

    const safeAdditionalPlaceholders = useMemo(() => {
        if (!Array.isArray(additionalPlaceholders)) return [];

        return additionalPlaceholders.filter(
            (x) => Array.isArray(x) && x.length >= 3 && typeof x[2] === 'string' && x[2].trim() !== ''
        );
    }, [additionalPlaceholders]);

    const audiencePlaceholders = useMemo(() => {
        return ALL_PLACEHOLDERS
            .filter(p => !Array.isArray(audienceType) || audienceType.includes(p.key))
            .map(p => [p.label, 'InsertText', p.token]);
    }, [audienceType]);

    const placeholderOptions = useMemo(() => {
        const base = isPublicAccess
            ? schoolPlaceHolder
            : [...schoolPlaceHolder, ...audiencePlaceholders];

        return [...base, ...safeAdditionalPlaceholders];
    }, [isPublicAccess, audiencePlaceholders, safeAdditionalPlaceholders]);

    const PlaceholderDropdown = useMemo(
        () => createDropdown('Placeholders', placeholderOptions),
        [placeholderOptions]
    );

    const handlePlaceholderSelect = (placeholder) => {
        setCurrentDescription((currentDescription ?? '') + placeholder);
    };

    const saveSelection = useCallback(() => {
        const sel = window.getSelection();
        if (!sel || sel.rangeCount === 0) return;

        const range = sel.getRangeAt(0);

        let node = range.startContainer;
        if (node.nodeType === Node.TEXT_NODE) node = node.parentElement;

        const editable = node?.closest?.('[contenteditable="true"]');
        if (!editable) return;

        editableRef.current = editable;
        selectionRef.current = range.cloneRange();
    }, []);

    const restoreSelection = useCallback(() => {
        const editable = editableRef.current;
        const range = selectionRef.current;
        if (!editable || !range) return;

        editable.focus();

        const sel = window.getSelection();
        if (!sel) return;

        sel.removeAllRanges();
        sel.addRange(range);
    }, []);

    const applyColorCommand = useCallback((color) => {
        setTextColor(color);

        restoreSelection();

        document.execCommand('styleWithCSS', false, true);
        document.execCommand('foreColor', false, color);
    }, [restoreSelection]);

    const rgbToHex = (value) => {
        if (!value) return null;
        if (value.startsWith('#')) return value;

        const m = value.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/i);
        if (!m) return null;

        const toHex = (n) => Number(n).toString(16).padStart(2, '0');
        return `#${toHex(m[1])}${toHex(m[2])}${toHex(m[3])}`;
    };

    const updateTextColorFromSelection = useCallback(() => {
        const sel = window.getSelection();
        if (!sel || sel.rangeCount === 0) return;

        const range = sel.getRangeAt(0);
        let node = range.startContainer;

        if (node.nodeType === Node.TEXT_NODE) node = node.parentElement;
        if (!(node instanceof Element)) return;

        const editable = node.closest?.('[contenteditable="true"]');
        if (!editable) return;

        editableRef.current = editable;

        const computed = window.getComputedStyle(node).color;
        const hex = rgbToHex(computed);

        if (hex) setTextColor(hex);
    }, []);

    const handleToggleColorPickerMouseDown = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();

        saveSelection();
        setColorAnchorEl(prev => (prev ? null : e.currentTarget));
    }, [saveSelection]);

    const warningInfo = useMemo(() => {
        if (Array.isArray(safeAdditionalPlaceholders) && safeAdditionalPlaceholders.length > 0) {
            return {show: false};
        }

        const html = currentDescription ?? '';
        const allowedKeys = new Set(Array.isArray(audienceType) ? audienceType : []);

        const tokenDefs = [
            {
                token: '@SchoolName',
                audienceAgnostic: true
            },

            ...ALL_PLACEHOLDERS.map(p => ({
                key: p.key,
                token: String(p.token).trim(),
                audienceAgnostic: false,
            })),

            {
                key: 'parent',
                token: '@Parent',
                audienceAgnostic: false,
                legacy: true
            },
            {
                key: 'employee',
                token: '@Employee',
                audienceAgnostic: false,
                legacy: true
            },
        ];

        const used = tokenDefs.filter(d => placeholderIsPresent(html, d.token, !!d.legacy));

        if (isPublicAccess) {
            const invalidForPublic = used.filter(d => d.token !== '@SchoolName');
            if (!invalidForPublic.length) return {show: false};

            return {
                show: true,
                message: 'Public forms can only use the @SchoolName placeholder. Remove the following placeholders',
                invalidTokens: invalidForPublic.map(x => x.token),
            };
        }

        const invalid = used.filter(d => {
            if (d.audienceAgnostic) return false;
            if (!d.key) return false;
            return !allowedKeys.has(d.key);
        });

        if (!invalid.length) return {show: false};

        const audienceLabel = `${getAudienceLabel(audience)} audience`;

        if (audience === FORM_AUDIENCE_TYPE.externalUsers) {
            const recipientLabel = getRecipientTypeLabel(recipientType);

            if (recipientLabel) {
                return {
                    show: true,
                    message: `This text contains placeholders that do not match the selected ${audienceLabel} (recipient type: ${recipientLabel})`,
                    invalidTokens: invalid.map(x => x.token),
                };
            }

            return {
                show: true,
                message: `This text contains placeholders that do not match the selected ${audienceLabel}. Select a recipient type to validate placeholders properly`,
                invalidTokens: invalid.map(x => x.token),
            };
        }

        return {
            show: true,
            message: `This text contains placeholders that do not match the selected ${audienceLabel}`,
            invalidTokens: invalid.map(x => x.token),
        };
    }, [
        currentDescription,
        audience,
        recipientType,
        audienceType,
        safeAdditionalPlaceholders,
        isPublicAccess
    ]);

    const stopDoubleClickBubbling = useCallback((e) => {
        e.stopPropagation();
    }, []);

    const stopClickBubbling = useCallback((e) => {
        e.stopPropagation();
    }, []);

    return (
        <Box sx={{pt: 1}}>
            {warningInfo?.show && (
                <Alert severity="warning" sx={{mb: 1}}>
                    {warningInfo.message}
                    {Array.isArray(warningInfo.invalidTokens) && warningInfo.invalidTokens.length > 0
                        ? `: ${Array.from(new Set(warningInfo.invalidTokens)).join(', ')}.`
                        : ''}
                </Alert>
            )}

            <Box
                onDoubleClick={stopDoubleClickBubbling}
                onClick={stopClickBubbling}
                onMouseUpCapture={updateTextColorFromSelection}
                onKeyUpCapture={updateTextColorFromSelection}
            >
                <EditorProvider>
                    <Editor
                        disabled={isFormLive}
                        value={currentDescription}
                        onChange={e => setCurrentDescription(e.target.value)}
                        containerProps={{
                            style: {
                                ...theme.typography.body1,
                                color: theme.palette.text.primary,
                                cursor: isFormLive ? 'not-allowed' : 'text',
                                ...style.editor,
                            },
                        }}
                    >
                        <Toolbar style={{...style.toolbar}}>
                            <BtnUndo/>
                            <BtnRedo/>
                            <Separator/>
                            <BtnBold/>
                            <BtnItalic/>
                            <BtnUnderline/>
                            <BtnStrikeThrough/>

                            <ToggleColorPickerButton
                                onMouseDown={handleToggleColorPickerMouseDown}
                                color={textColor}
                            />

                            <ColorPicker
                                open={isColorPickerOpen}
                                anchorEl={colorAnchorEl}
                                applyColorCommand={applyColorCommand}
                                onClose={() => setColorAnchorEl(null)}
                            />

                            <Separator/>
                            <BtnNumberedList/>
                            <BtnBulletList/>
                            <BtnAlignLeft/>
                            <BtnAlignCenter/>
                            <BtnAlignRight/>
                            <Separator/>
                            <BtnLink/>
                            <BtnClearFormatting/>
                            <Separator/>
                            <BtnStyles/>
                            <Separator/>
                            <PlaceholderDropdown onChange={value => handlePlaceholderSelect(value)}/>
                        </Toolbar>
                    </Editor>
                </EditorProvider>
            </Box>
        </Box>
    );
};

RichEditor.defaultProps = {
    style: {
        editor: {},
        toolbar: {},
    },
    additionalPlaceholders: [],
};

RichEditor.propTypes = {
    currentDescription: PropTypes.string.isRequired,
    setCurrentDescription: PropTypes.func.isRequired,
    isFormLive: PropTypes.bool.isRequired,
    accessLevel: PropTypes.number.isRequired,
    style: PropTypes.object,
    additionalPlaceholders: PropTypes.array,
    audience: PropTypes.number,
    recipientType: PropTypes.string,
};

export default RichEditor;
